import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell,
} from 'recharts';
import { fmtMoney, fmtMoneyShort, monthLabelShort, fmtMonth } from './format';
import { card, chart } from '../../theme';

/**
 * SpendTrend — monthly spend for the CURRENT FILTER SELECTION.
 * Split by payment type, because that is the one decomposition that always
 * applies and always reconciles to the net total.
 */
export default function SpendTrend({ rows, loading, onSelectMonth, selectedMonths = [] }) {
  const data = (rows || []).map((r) => ({
    month: r.month,
    label: monthLabelShort(r.month),
    card: Number(r.card_amount || 0),
    reimbursement: Number(r.reimbursement_amount || 0),
    net: Number(r.net_amount || 0),
  }));

  if (!loading && data.length === 0) {
    return <div style={s.card}><div style={s.empty}>No transactions match these filters.</div></div>;
  }

  const selected = new Set(selectedMonths);

  return (
    <div style={{ ...s.card, opacity: loading ? 0.55 : 1 }}>
      <div style={s.header}>
        <div>
          <div style={s.title}>Monthly Spend</div>
          <div style={s.subtitle}>
            {data.length} month{data.length !== 1 ? 's' : ''} in selection · click a bar to filter to that month
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}
                  onClick={(state) => {
                    // See RankedBarChart: Recharts 3 gives a chart-level
                    // onClick only activeIndex / activeLabel, never
                    // activePayload. Resolve the month from our own data.
                    const i = Number(state?.activeIndex);
                    const row = Number.isInteger(i) && i >= 0 && i < data.length
                      ? data[i]
                      : data.find((d) => d.label === state?.activeLabel);
                    if (row?.month && onSelectMonth) onSelectMonth(row.month);
                  }}>
          <CartesianGrid vertical={false} stroke={chart.grid} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: chart.axisText }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={fmtMoneyShort} tick={{ fontSize: 10, fill: chart.axisText }}
                 axisLine={false} tickLine={false} width={56} />
          <Tooltip
            wrapperStyle={{ pointerEvents: 'none' }}
            isAnimationActive={false}
            cursor={{ fill: 'rgba(27,42,74,0.05)' }}
            formatter={(v, n) => [fmtMoney(v), n === 'card' ? 'Company Card' : 'Reimbursement']}
            labelFormatter={(_l, p) => fmtMonth(p?.[0]?.payload?.month)}
            contentStyle={{ fontSize: '12px', borderRadius: '8px', border: `1px solid ${chart.tooltipBorder}` }}
          />
          <Legend iconType="circle" iconSize={7}
                  formatter={(v) => <span style={{ fontSize: 11, color: '#6B7280' }}>
                    {v === 'card' ? 'Company Card' : 'Reimbursement'}</span>} />
          <Bar dataKey="card" stackId="a" fill={chart.emphasis} radius={[0, 0, 0, 0]}>
            {data.map((d) => (
              <Cell key={d.month} fill={selected.size && !selected.has(d.month) ? chart.deselected : chart.emphasis}
                    cursor="pointer" />
            ))}
          </Bar>
          <Bar dataKey="reimbursement" stackId="a" fill={chart.sequential[1]} radius={[4, 4, 0, 0]}>
            {data.map((d) => (
              <Cell key={d.month} fill={selected.size && !selected.has(d.month) ? chart.deselectedLight : chart.sequential[1]}
                    cursor="pointer" />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const s = {
  card:     { ...card.sm, padding: '16px 16px 8px', transition: 'opacity 0.15s' },
  header:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' },
  title:    { fontSize: '13px', fontWeight: 600, color: '#111827' },
  subtitle: { fontSize: '11px', color: '#9CA3AF', marginTop: '2px' },
  empty:    { padding: '48px 16px', textAlign: 'center', fontSize: '13px', color: '#9CA3AF' },
};
